import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSectionVisibilityByKey } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { isAdministrator } from '../lib/platformRole';

interface ProtectedSectionProps {
  sectionKey: string;
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * ProtectedSection Component
 * Wraps a page/section and checks if it's accessible based on Global Settings.
 *
 * Access rules:
 * - isActive=false → nobody can access (redirect to dashboard)
 * - isActive=true, isVisible=false → only admins can access
 * - isActive=true, isVisible=true → everyone can access
 */
export function ProtectedSection({ sectionKey, children, redirectTo = '/dashboard' }: ProtectedSectionProps) {
  const navigate = useNavigate();
  const { data: user } = useAuth();
  const [isChecking, setIsChecking] = useState(true);
  const [canAccess, setCanAccess] = useState(false);

  useEffect(() => {
    checkAccess();
  }, [sectionKey, user]);

  const checkAccess = async () => {
    try {
      const visibility = await getSectionVisibilityByKey({ sectionKey });

      // If no visibility setting exists, default to accessible
      if (!visibility) {
        setCanAccess(true);
        setIsChecking(false);
        return;
      }

      // Active takes precedence: if not active, nobody can access
      if (!visibility.isActive) {
        navigate(redirectTo, { replace: true });
        return;
      }

      // If active but not visible, only admins can access
      if (!visibility.isVisible) {
        if (isAdministrator(user)) {
          setCanAccess(true);
        } else {
          navigate(redirectTo, { replace: true });
          return;
        }
      } else {
        // Active and visible: everyone can access
        setCanAccess(true);
      }
    } catch (error) {
      console.error(`Failed to check access for section ${sectionKey}:`, error);
      // On error, redirect to be safe
      navigate(redirectTo, { replace: true });
    } finally {
      setIsChecking(false);
    }
  };

  // Show loading state while checking
  if (isChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Only render children if access is granted
  if (!canAccess) {
    return null;
  }

  return <>{children}</>;
}

/**
 * withSectionProtection HOC
 * Higher-order component to wrap a page component with section protection
 *
 * Usage:
 * export default withSectionProtection(BuySellPage, 'buy-sell');
 */
export function withSectionProtection<P extends object>(
  Component: React.ComponentType<P>,
  sectionKey: string,
  redirectTo?: string
) {
  return function ProtectedComponent(props: P) {
    return (
      <ProtectedSection sectionKey={sectionKey} redirectTo={redirectTo}>
        <Component {...props} />
      </ProtectedSection>
    );
  };
}



